import React, { useState } from 'react';
import styled from 'styled-components';
import { 
  FileText, 
  Download, 
  Calendar, 
  Search,
  Filter,
  ChevronRight,
  FileDown,
  Newspaper,
  Loader2,
  FileX
} from 'lucide-react';
import { useCommuniques } from '../hooks/useCommuniques';

const PageContainer = styled.div`
  padding-top: 90px;
  min-height: 100vh;
  background: var(--background-light);
`;

const Hero = styled.section`
  background: linear-gradient(135deg, var(--primary-blue) 0%, #004d8f 100%);
  padding: 4rem 0;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  }
`;

const HeroContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 1;
  text-align: center;
`;

const HeroTitle = styled.h1`
  font-family: var(--font-secondary);
  font-size: 3rem;
  color: var(--white);
  margin-bottom: 1rem;
  font-weight: 800;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.9);
  max-width: 600px;
  margin: 0 auto;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem 4rem;
`;

const ContentWrapper = styled.div`
  margin-top: -2rem;
  position: relative;
  z-index: 2;
`;

const SearchFilterBar = styled.div`
  background: var(--white);
  border-radius: var(--radius-xl);
  padding: 1.5rem;
  box-shadow: var(--shadow-md);
  margin-bottom: 2rem;
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  border: 1px solid var(--border-light);

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const SearchBox = styled.div`
  flex: 1;
  position: relative;
  min-width: 250px;

  svg {
    position: absolute;
    left: 1rem;
    top: 50%;
    transform: translateY(-50%);
    color: var(--text-light);
  }

  input {
    width: 100%;
    padding: 0.875rem 1rem 0.875rem 2.75rem;
    border: 2px solid var(--border-light);
    border-radius: var(--radius-md);
    font-size: 0.95rem;
    transition: var(--transition);

    &:focus {
      outline: none;
      border-color: var(--primary-blue);
    }

    &::placeholder {
      color: var(--text-light);
    }
  }
`;

const FilterSelect = styled.select`
  padding: 0.875rem 2.5rem 0.875rem 1rem;
  border: 2px solid var(--border-light);
  border-radius: var(--radius-md);
  font-size: 0.95rem;
  background: var(--white);
  cursor: pointer;
  min-width: 180px;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;

  &:focus {
    outline: none;
    border-color: var(--primary-blue);
  }
`;

const StatsBar = styled.div`
  display: flex;
  gap: 2rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  background: var(--white);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--border-light);

  .number {
    font-size: 1.5rem;
    font-weight: 800;
    color: var(--primary-blue);
    font-family: var(--font-secondary);
  }

  .label {
    font-size: 0.9rem;
    color: var(--text-medium);
  }

  svg {
    color: var(--accent-gold);
  }
`;

const CommuniquesGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const CommuniqueCard = styled.div`
  background: var(--white);
  border-radius: var(--radius-lg);
  padding: 1.5rem;
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--border-light);
  display: flex;
  align-items: center;
  gap: 1.5rem;
  transition: var(--transition);

  &:hover {
    box-shadow: var(--shadow-lg);
    border-color: var(--accent-gold);
    transform: translateX(8px);
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const FileIcon = styled.div`
  width: 60px;
  height: 70px;
  background: linear-gradient(135deg, #dc2626, #ef4444);
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--white);
  flex-shrink: 0;
  position: relative;

  &::after {
    content: 'PDF';
    position: absolute;
    bottom: 6px;
    font-size: 0.65rem;
    font-weight: 800;
    background: rgba(255,255,255,0.9);
    color: #dc2626;
    padding: 1px 6px;
    border-radius: 3px;
  }
`;

const CommuniqueInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const CommuniqueTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--text-dark);
  margin-bottom: 0.5rem;
  line-height: 1.4;
`;

const CommuniqueMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  flex-wrap: wrap;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.85rem;
  color: var(--text-light);

  svg {
    color: var(--primary-blue);
    opacity: 0.6;
  }
`;

const FileSize = styled.span`
  font-size: 0.8rem;
  color: var(--text-light);
  background: var(--background-light);
  padding: 0.25rem 0.75rem;
  border-radius: var(--radius-full);
`;

const DownloadButton = styled.a`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--primary-blue);
  color: var(--white);
  padding: 0.875rem 1.5rem;
  border-radius: var(--radius-md);
  font-weight: 600;
  font-size: 0.95rem;
  transition: var(--transition);
  flex-shrink: 0;

  &:hover {
    background: var(--accent-gold);
    color: var(--primary-blue-dark);
  }

  svg {
    transition: var(--transition);
  }

  &:hover svg {
    transform: translateY(2px);
  }

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  background: var(--white);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-sm);

  svg {
    color: var(--border-light);
    margin-bottom: 1rem;
  }

  h3 {
    font-size: 1.25rem;
    color: var(--text-dark);
    margin-bottom: 0.5rem;
  }

  p {
    color: var(--text-light);
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 2rem;
`;

const PageButton = styled.button`
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-md);
  font-weight: 600;
  transition: var(--transition);
  background: ${props => props.$active ? 'var(--primary-blue)' : 'var(--white)'};
  color: ${props => props.$active ? 'var(--white)' : 'var(--text-dark)'};
  border: 1px solid ${props => props.$active ? 'var(--primary-blue)' : 'var(--border-light)'};

  &:hover {
    border-color: var(--primary-blue);
    color: ${props => props.$active ? 'var(--white)' : 'var(--primary-blue)'};
  }
`;



const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return 'Taille inconnue';
  const k = 1024;
  const sizes = ['Octets', 'Ko', 'Mo', 'Go'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const Communiques = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const { communiques, loading, error } = useCommuniques();
  
  // Debug
  console.log('Communiques component:', { loading, error, count: communiques.length });

  const filteredCommuniques = communiques.filter(comm => {
    const matchesSearch = comm.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filter === 'all' || comm.category === filter;
    return matchesSearch && matchesFilter;
  });

  const categories = ['all', ...new Set(communiques.map(c => c.category))];

  return (
    <PageContainer>
      <Hero>
        <HeroContent>
          <HeroTitle>Communiqués</HeroTitle>
          <HeroSubtitle>
            Tous les communiqués de presse et publications officielles de la Médiature
          </HeroSubtitle>
        </HeroContent>
      </Hero>

      <Container>
        <ContentWrapper>
          {/* Barre de recherche et filtres */}
          <SearchFilterBar>
            <SearchBox>
              <Search size={20} />
              <input 
                type="text" 
                placeholder="Rechercher un communiqué..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </SearchBox>
            <FilterSelect 
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            >
              <option value="all">Toutes les catégories</option>
              {categories.filter(c => c !== 'all').map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </FilterSelect>
          </SearchFilterBar>

          {/* Loading state */}
          {loading && (
            <EmptyState>
              <Loader2 size={48} className="animate-spin" />
              <h3>Chargement...</h3>
            </EmptyState>
          )}

          {/* Error state */}
          {error && (
            <EmptyState>
              <FileX size={48} />
              <h3>Erreur de chargement</h3>
              <p>{error}</p>
              <p style={{ fontSize: '0.85rem', marginTop: '1rem', color: '#6B7280' }}>
                Vérifiez les règles Firestore dans la console Firebase
              </p>
            </EmptyState>
          )}

          {/* Stats */}
          {!loading && !error && (
            <StatsBar>
              <StatItem>
                <FileText size={24} />
                <div>
                  <div className="number">{communiques.length}</div>
                  <div className="label">Communiqués</div>
                </div>
              </StatItem>
              <StatItem>
                <Download size={24} />
                <div>
                  <div className="number">
                    {communiques.reduce((acc, c) => acc + (c.downloads || 0), 0).toLocaleString()}
                  </div>
                  <div className="label">Téléchargements</div>
                </div>
              </StatItem>
            </StatsBar>
          )}

          {/* Liste des communiqués */}
          {!loading && !error && filteredCommuniques.length > 0 ? (
            <CommuniquesGrid>
              {filteredCommuniques.map(communique => (
                <CommuniqueCard key={communique.id}>
                  <FileIcon>
                    <FileDown size={28} />
                  </FileIcon>
                  <CommuniqueInfo>
                    <CommuniqueTitle>{communique.title}</CommuniqueTitle>
                    <CommuniqueMeta>
                      <MetaItem>
                        <Calendar size={14} />
                        {communique.date.toLocaleDateString('fr-FR', { 
                          day: 'numeric', 
                          month: 'long', 
                          year: 'numeric' 
                        })}
                      </MetaItem>
                      <MetaItem>
                        <Newspaper size={14} />
                        {communique.category}
                      </MetaItem>
                      <FileSize>{formatFileSize(communique.fileSize)}</FileSize>
                    </CommuniqueMeta>
                  </CommuniqueInfo>
                  {communique.pdfUrl ? (
                    <DownloadButton href={communique.pdfUrl} target="_blank" rel="noopener noreferrer">
                      <Download size={20} />
                      Télécharger
                    </DownloadButton>
                  ) : (
                    <DownloadButton as="span" style={{ opacity: 0.5, cursor: 'not-allowed' }}>
                      <FileX size={20} />
                      Indisponible
                    </DownloadButton>
                  )}
                </CommuniqueCard>
              ))}
            </CommuniquesGrid>
          ) : !loading && !error && (
            <EmptyState>
              <FileText size={64} />
              <h3>Aucun communiqué trouvé</h3>
              <p>Essayez de modifier vos critères de recherche</p>
            </EmptyState>
          )}

          {/* Pagination */}
          {filteredCommuniques.length > 0 && (
            <Pagination>
              <PageButton>←</PageButton>
              <PageButton $active>1</PageButton>
              <PageButton>2</PageButton>
              <PageButton>3</PageButton>
              <PageButton>→</PageButton>
            </Pagination>
          )}
        </ContentWrapper>
      </Container>
    </PageContainer>
  );
};

export default Communiques;
